import React, { useState } from 'react';
import { useDivisions, useDeleteDivision } from '../../hooks/use-divisions';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/form';
import { DivisionFormModal } from './division-form-modal';
import { Division } from '../../types';
import { format } from 'date-fns';

interface DivisionTableProps {
  onEditDivision?: (division: Division) => void;
}

export function DivisionTable({ onEditDivision }: DivisionTableProps) {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDivision, setEditingDivision] = useState<Division | undefined>();

  const { data, isLoading, error } = useDivisions({ 
    search: search || undefined, 
    active: activeFilter 
  });
  const deleteDivisionMutation = useDeleteDivision();

  const handleDeleteDivision = async (divisionId: string, divisionName: string) => {
    if (window.confirm(`Are you sure you want to delete the division "${divisionName}"? This action cannot be undone.`)) {
      try {
        await deleteDivisionMutation.mutateAsync(divisionId);
      } catch (error) {
        console.error('Failed to delete division:', error);
        alert('Failed to delete division. Please try again.');
      }
    }
  };

  const handleEditDivision = (division: Division) => {
    setEditingDivision(division);
    onEditDivision?.(division);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-secondary-600 dark:text-secondary-400">Loading divisions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-600 dark:text-red-400">Failed to load divisions. Please try again.</div>
      </div>
    );
  }

  const divisions = data?.divisions || [];

  return (
    <div className="space-y-4">
      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Search divisions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sm:w-64"
          />
          <select
            value={activeFilter === undefined ? 'all' : activeFilter ? 'active' : 'inactive'}
            onChange={(e) => {
              const value = e.target.value;
              setActiveFilter(value === 'all' ? undefined : value === 'active');
            }}
            className="rounded-md border border-secondary-300 dark:border-secondary-600 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
          >
            <option value="all">All Divisions</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>
        
        <Button onClick={() => setShowCreateModal(true)}>
          Create Division
        </Button>
      </div>

      {/* Results Summary */}
      <div className="text-sm text-secondary-600 dark:text-secondary-400">
        Showing {divisions.length} of {data?.totalCount || 0} divisions
      </div>

      {/* Divisions Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Code</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {divisions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-12 text-secondary-500 dark:text-secondary-400">
                {search || activeFilter !== undefined 
                  ? 'No divisions match your current filters.' 
                  : 'No divisions found. Create your first division to get started.'
                }
              </TableCell>
            </TableRow>
          ) : (
            divisions.map((division) => (
              <TableRow 
                key={division.id}
                onClick={() => handleEditDivision(division)}
                className="cursor-pointer hover:bg-secondary-50 dark:hover:bg-secondary-700"
              >
                <TableCell className="font-medium">
                  <div>{division.name}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-xs text-secondary-400 dark:text-secondary-500 font-mono">{division.id}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(division.id);
                      }}
                      className="text-secondary-400 dark:text-secondary-500 hover:text-secondary-600 dark:hover:text-secondary-300"
                      title="Copy division ID"
                    >
                      <i className="fa-regular fa-copy text-xs"></i>
                    </button>
                  </div>
                </TableCell>
                <TableCell>
                  {division.code && (
                    <Badge variant="default" size="sm">
                      {division.code}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {division.description || '—'}
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={division.active ? 'success' : 'warning'}
                    size="sm"
                  >
                    {division.active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-secondary-500 dark:text-secondary-400">
                  {format(new Date(division.createdAt), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDivision(division);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDivision(division.id, division.name);
                      }}
                      disabled={deleteDivisionMutation.isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Create Division Modal */}
      <DivisionFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit Division Modal */}
      <DivisionFormModal
        key={editingDivision?.id || 'edit'}
        isOpen={!!editingDivision}
        onClose={() => setEditingDivision(undefined)}
        division={editingDivision}
      />
    </div>
  );
}
